import React, { PureComponent, Fragment } from 'react'
import Select from 'react-select'
import moment from 'moment-timezone'
import classNames from 'classnames'
import find from 'lodash/find'
import includes from 'lodash/includes'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import pull from 'lodash/pull'
import filter from 'lodash/filter'
import compact from 'lodash/compact'
import MenuMonthFilterControls from './menuMonthFilterControls'
import Modal from '../shared/modal'
import Calendar from '../shared/calendar'
import SectionTitle from '../shared/sectionTitle'
import { monthNames, customSelectOption, customSelectValue } from '../shared/common'
import { tenantNow } from '../../utils/tenantDateTime'
import PlusMinusWrapper from '../shared/plusMinusWrapper'
import { t } from '../../i18n'
import Tooltip from '@mui/material/Tooltip'

const scope = 'recipes'
const defaultAmount = 10

class DuplicatingCalendar extends React.Component {
  state = {
    year: moment().startOf('month').year(),
    month: moment().startOf('month').month() + 1
  }

  updateYearAndMonthFilter = (year, month) => {
    this.setState({ year, month })
  }

  renderDateCellContent = (date) => {
    if (!date) return

    const { originalDate, selectedDates } = this.props
    // The kitchen's today, not the viewer's - an admin a few hours ahead would otherwise see
    // "today" highlighted on the cell the kitchen still calls tomorrow.
    const currentDate = tenantNow(this.props.tenantTz).format('YYYY-MM-DD')

    const classes = classNames(
      'calendar-date',
      {
        'selected': includes(selectedDates, date),
        'today': date === currentDate,
        'original': date === originalDate
      }
    )

    return (
      <div className={classes} onClick={() => this.props.toggleSelectedDate(date)}>
        <span>{moment.tz(date, this.props.tenantTz).format(this.props.dayMonthFormat)}</span>
      </div>
    )
  }

  render = () => {
    const { selectedDates } = this.props
    const { month, year } = this.state

    return (
      <div>
        <div className='d-flex justify-content-end mb-4'>
          <MenuMonthFilterControls
            year={this.state.year}
            month={this.state.month}
            updateYearAndMonthFilter={this.updateYearAndMonthFilter}
          />
        </div>
        <Calendar
          className='table-borderless table-calendar'
          month={month}
          year={year}
          renderDateCell={(date, idx) => <td key={idx}>{this.renderDateCellContent(date)}</td>}
        />
      </div>
    )
  }
}

class MenuModal extends PureComponent {
  constructor(props) {
    super(props)

    this.blankMenuRecipe = {
      id: null,
      recipe_id: null,
      recipe_name: '',
      amount: 0,
    }

    this.state = {
      duplicating: false,
      selectedDates: [],
      duplicateDates: [],
      date: props.date,
      menu: props.menuItem,
      menuRecipes: (props.menuItem && props.menuItem.menu_recipes.length > 0)
        ? props.menuItem.menu_recipes
        : [this.blankMenuRecipe]
    }
  }

  handleSelectChange = (index, opt) => {
    let menuRecipes = [...this.state.menuRecipes]

    if (opt) {
      menuRecipes[index] = {
        ...menuRecipes[index],
        recipe_id: opt.value,
        recipe_name: opt.label,
        amount: this.findRecipeAmountById(opt.value),
      }
    } else {
      menuRecipes[index] = this.blankMenuRecipe
    }

    this.setState({ menuRecipes })
  }

  handleRecipeAmountChange = (index, value) => {
    let menuRecipes = [ ...this.state.menuRecipes ]
    value = value < 0 ? 0 : value

    menuRecipes[index] = { ...menuRecipes[index], amount: value }

    this.setState({ menuRecipes })
  }

  findRecipeAmountById = (recipeId) => {
    const recipe = find(this.props.recipes, r => r.id === recipeId)
    const averageOrdersPerDay = parseInt(recipe.average_orders_per_day, 10)

    return averageOrdersPerDay > 0 ? averageOrdersPerDay : defaultAmount
  }

  toggleSelectedDate = (date) => {
    if (date === this.state.date) return
    let selectedDates = [...this.state.selectedDates]

    if (includes(selectedDates, date)) {
      pull(selectedDates, date)
    } else {
      selectedDates.push(date)
    }

    this.setState({ selectedDates })
  }

  renderMainActions = () => {
    const { menu, menuRecipes, date, duplicateDates } = this.state
    const recipeIds = compact(map(menuRecipes, mr => mr.recipe_id))

    return (
      <Fragment>
        <button
          disabled={isEmpty(recipeIds)}
          className='btn btn-primary'
          onClick={() => this.props.saveMenu({
            dates: [date, ...duplicateDates],
            menu: { ...menu, recipes: map(menuRecipes, mr => ({ id: mr.recipe_id, amount: mr.amount })) }
          })}
        >
          Save
        </button>
        <button
          className='btn btn-secondary'
          onClick={() => this.setState({ duplicating: true })}
        >
          Duplicate
        </button>
        {menu && menu.id &&
          <button
            className='btn btn-danger'
            onClick={() => this.props.destroyMenu(menu.id)}
          >
            Delete
          </button>
        }
      </Fragment>
    )
  }

  renderDuplicateActions = () => {
    const { selectedDates, duplicateDates } = this.state

    return (
      <Fragment>
        <button
          className='btn btn-primary'
          onClick={() => this.setState({ duplicating: false, duplicateDates: selectedDates.sort() })}
        >
          {`Duplicate (${selectedDates.length})`}
        </button>
        <button
          className='btn btn-secondary'
          onClick={() => this.setState({ duplicating: false, selectedDates: duplicateDates })}
        >
          Cancel
        </button>
      </Fragment>
    )
  }

  selectOptions = () => (
    map(this.props.recipes, ({ id, name, file_url }) =>
      ({ value: id, label: name, imageUrl: file_url })
    )
  )

  visibleSelectOptions = () => (
    filter(this.selectOptions(), opt =>
      !find(this.state.menuRecipes, recipe => recipe.recipe_id === opt.value)
    )
  )

  selectValue = (menuRecipe) => (
    (menuRecipe && menuRecipe.recipe_id)
      ? find(this.selectOptions(), opt => opt.value === menuRecipe.recipe_id)
      : {}
  )

  addNewRecipe = () => {
    const { menuRecipes } = this.state
    if (this.selectOptions().length === menuRecipes.length) return

    this.setState({
      menuRecipes: [...menuRecipes, this.blankMenuRecipe]
    })
  }

  deleteRecipe = (index) => {
    let menuRecipes = [...this.state.menuRecipes]
    if (menuRecipes.length < 2) return

    menuRecipes.splice(index, 1)

    this.setState({ menuRecipes })
  }

  renderForm = (date) => {
    const { duplicateDates, menuRecipes } = this.state
    const { dayMonthFormat, tenantTz } = this.props

    // Judged against the kitchen's day, so a viewer in a later timezone is not blocked from
    // editing a day the kitchen has not finished yet.
    const isInPast = moment.tz(date, tenantTz) < tenantNow(tenantTz).startOf('day')
    const addRecipeDisabled = this.selectOptions().length === menuRecipes.length || isInPast
    const deleteRecipeDisabled = menuRecipes.length < 2 || isInPast

    return (
      <form>
        <div className='form-group' data-contains='menu-item-modal-recipe-select'>
          <p className='mb-0'><label>Select recipes</label></p>
          {map(this.state.menuRecipes, (menuRecipe, index) =>
            <div className={`d-flex align-items-center mb-3 index-${index}`} key={index}>
              <Select
                isDisabled={isInPast}
                className='react-select flex-1'
                classNamePrefix='react-select'
                placeholder='Select recipe'
                value={this.selectValue(menuRecipe)}
                onChange={(opt) => this.handleSelectChange(index, opt)}
                options={this.visibleSelectOptions()}
                components={{
                  Option: customSelectOption,
                  SingleValue: customSelectValue
                }}
              />
              <PlusMinusWrapper
                className='ms-3'
                name='amount'
                step={ 1 }
                value={menuRecipe.amount}
                onChange={(_name, value) => { this.handleRecipeAmountChange(index, value) }}
              >
                <Tooltip
                  title={t('recipe_products.amount', { scope })}
                  placement='top'
                  arrow
                >
                  <div className='subwrapper'>
                    <input
                      type='number'
                      name='amount'
                      value={menuRecipe.amount}
                      min="0"
                      onChange={(e) => {
                        e.preventDefault()
                        this.handleRecipeAmountChange(index, Number(e.target.value))
                      }}
                    />
                  </div>
                </Tooltip>
              </PlusMinusWrapper>
              <i
                className={`button-delete fa fa-trash-o fa-2x ms-3 ${deleteRecipeDisabled ? 'disabled' : 'pointer'}`}
                onClick={() => this.deleteRecipe(index)}
                disabled={deleteRecipeDisabled}
              />
            </div>
          )}
          <span
            className={`button-add ${addRecipeDisabled ? 'disabled' : 'pointer'}`}
            onClick={this.addNewRecipe}
          >
            <i className='fa fa-plus-circle large me-2' />
            <span>Add</span>
          </span>
        </div>
        {duplicateDates.length > 0 &&
          <div className='duplicate-dates-list my-2'>
            <span className='me-2'>Duplicated to:</span>
            {map(duplicateDates, date =>
              <span key={date} className='badge bg-light me-2'>{moment.tz(date, tenantTz).format(dayMonthFormat)}</span>
            )}
          </div>
        }
      </form>
    )
  }

  render = () => {
    const { duplicating, date, selectedDates } = this.state
    const { dayMonthFormat, tenantTz } = this.props
    const momentDate = moment(date)

    return (
      <Modal
        onClose={this.props.closeModal}
        footerContent={duplicating ? this.renderDuplicateActions() : this.renderMainActions()}
        footerClassName='me-auto'
        widen={true}
        headerContent={
          <SectionTitle
            rows={[monthNames[momentDate.month()], momentDate.date()]}
            dataContains={momentDate.format('YYYY-MM-DD')}
            paddingLeft={0}
          />
        }
      >
        {duplicating
          ? <DuplicatingCalendar
              originalDate={date}
              selectedDates={selectedDates}
              toggleSelectedDate={this.toggleSelectedDate}
              dayMonthFormat={dayMonthFormat}
              tenantTz={tenantTz}
            />
          : this.renderForm(date)
        }
      </Modal>
    )
  }
}

export default MenuModal
